import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { Filter, FilterBuilder, FilterPage, FilterSort, Status, StorageRegime, StorageRegimeService, translateCommonErrorStatus } from '@core/data';

// Paginated, sorted, searchable listing of this org's own Custom Regimes — backs the Storage
// Regimes index table. Deliberately separate from StorageRegimeState: that one still holds the
// full, unpaginated Custom+Legacy list the regime-builder dialog needs for its "start from a
// Legacy Regime" selector, and CreateCustomRegime/UpdateCustomRegime/DeleteCustomRegime continue
// to patch it directly. This state is reloaded explicitly by the index view after those succeed.

export class InitStorageRegimeIndex {
    static readonly type = '[StorageRegimeIndex] Init';
    constructor(public orgKey: string) {}
}

export class LoadStorageRegimeIndex {
    static readonly type = '[StorageRegimeIndex] Load';
}

export class PageStorageRegimeIndex {
    static readonly type = '[StorageRegimeIndex] Page';
    constructor(public index: number, public size: number) {}
}

export class SortStorageRegimeIndex {
    static readonly type = '[StorageRegimeIndex] Sort';
    constructor(public column: string, public order = 'asc') {}
}

export class SearchStorageRegimeIndex {
    static readonly type = '[StorageRegimeIndex] Search';
    constructor(public search: string) {}
}

export interface StorageRegimeIndexStateModel {
    status: Status;
    orgKey: string;
    data: StorageRegime[];
    total: number;
    page: FilterPage;
    pageSizeOptions: number[];
    sort: FilterSort;
    sortOptions: { id: string, label: string }[];
    search: string;
}

const DEFAULTS: StorageRegimeIndexStateModel = {
    status: Status.UNINITIALIZED,
    orgKey: null,
    data: [],
    total: 0,
    page: {
        index: 0,
        size: 10,
    },
    pageSizeOptions: [10, 25, 50],
    sort: {
        column: 'label',
        order: 'asc',
    },
    //? SORT OPTIONS MUST MATCH StorageRegime::$sortable on the API side
    sortOptions: [
        { id: 'label', label: 'Label' },
        { id: 'stages', label: 'Stages' },
        { id: 'updatedAt', label: 'Updated At' },
        { id: 'createdAt', label: 'Created At' },
    ],
    search: null,
};

@State<StorageRegimeIndexStateModel>({
    name: 'storage_regime_index_state',
    defaults: DEFAULTS,
})
@Injectable()
export class StorageRegimeIndexState {

    @Selector()
    static state(state: StorageRegimeIndexStateModel) {
        return state;
    }

    constructor(
        private _storageRegimeService: StorageRegimeService,
    ) {}

    @Action(InitStorageRegimeIndex, { cancelUncompleted: true })
    init(ctx: StateContext<StorageRegimeIndexStateModel>, action: InitStorageRegimeIndex) {
        ctx.setState({
            ...DEFAULTS,
            orgKey: action.orgKey,
        });

        return ctx.dispatch(new LoadStorageRegimeIndex());
    }

    @Action(LoadStorageRegimeIndex, { cancelUncompleted: true })
    load(ctx: StateContext<StorageRegimeIndexStateModel>) {
        const state = ctx.getState();
        ctx.patchState({ status: Status.LOADING });

        const filter = this.getCurrentFilter(state);

        return this._storageRegimeService.queryCustom(state.orgKey, filter).pipe(
            tap(
                result => ctx.patchState({ status: Status.OK, data: result.data, total: result.total }),
                error => ctx.patchState({ status: translateCommonErrorStatus(error) }),
            ),
        );
    }

    @Action(PageStorageRegimeIndex)
    page(ctx: StateContext<StorageRegimeIndexStateModel>, action: PageStorageRegimeIndex) {
        ctx.patchState({
            page: {
                index: action.index,
                size: action.size,
            },
        });

        return ctx.dispatch(new LoadStorageRegimeIndex());
    }

    @Action(SortStorageRegimeIndex)
    sort(ctx: StateContext<StorageRegimeIndexStateModel>, action: SortStorageRegimeIndex) {
        const state = ctx.getState();

        ctx.patchState({
            sort: {
                column: action.column,
                order: action.order,
            },
            page: {
                index: 0,
                size: state.page.size,
            },
        });

        return ctx.dispatch(new LoadStorageRegimeIndex());
    }

    @Action(SearchStorageRegimeIndex)
    search(ctx: StateContext<StorageRegimeIndexStateModel>, action: SearchStorageRegimeIndex) {
        const state = ctx.getState();

        ctx.patchState({
            search: action.search,
            page: {
                index: 0,
                size: state.page.size,
            },
        });

        return ctx.dispatch(new LoadStorageRegimeIndex());
    }

    private getCurrentFilter(state: StorageRegimeIndexStateModel): Filter {
        const fb = new FilterBuilder();

        fb.setSort(state.sort);
        fb.setPage(state.page);
        if (state.search && state.search.trim()) fb.setQuery('$fuzzy', state.search);

        return fb.get();
    }
}
